import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import FormLayout from "ui/components/FormLayout";
import validateEmail, { isPublicEmail } from "utils/validateEmail";
import { HttpClient, ApiError } from "apiclient/ApiClient";
import { useAuth } from "auth/AuthProvider";
import useQueryParam from "hooks/useQueryParam";
import {
    SIGNIN_BUTTON_APPLE,
    SIGNIN_HEADER,
    SIGNUP_HEADER,
    SIGNIN_WITH_EMAIL_EMAIL,
    SIGNIN_WITH_EMAIL_EMPTY_EMAIL,
    SIGNIN_WITH_EMAIL_PUBLIC_EMAIL,
    SIGNIN_WITH_EMAIL_SUBMIT,
    SIGNIN_WITH_EMAIL_WRONG_EMAIL,
} from "config/config";

export default function FormSignin() {
    const [error, setError] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const auth = useAuth();
    const navigate = useNavigate();
    const loc = useLocation();
    const client = HttpClient();
    const isSignup = loc.pathname === '/signup';
    const prev_loc = loc.state?.from 
        ? `${loc.state.from.pathname}${loc.state.from.search || ''}`
        : "/";
    const accessToken = useQueryParam("access_token");
    
    React.useEffect(() => {
        if (accessToken) {
            auth.signin.withAccessToken(accessToken).then(() => {
                navigate(prev_loc);
            });
        } else if (auth.session) {
            navigate(prev_loc);
        }
    }, [accessToken, auth, navigate, prev_loc, auth.session]);
    
    const emailChanged = (email: string) => {
        setError("");
        setEmail(email);
    };
    
    const submitEmail = async () => {
        if (!email) {
            setError(SIGNIN_WITH_EMAIL_EMPTY_EMAIL);
            return;
        } else if (!validateEmail(email)) {
            setError(SIGNIN_WITH_EMAIL_WRONG_EMAIL);
            return;
        } else if (isPublicEmail(email)) {
            setError(SIGNIN_WITH_EMAIL_PUBLIC_EMAIL);
            return;
        }
        setLoading(true);
        
        try {
            // If we came from a specific page (like weekly-report), include it as redirect_url
            const redirectParam = prev_loc !== '/' 
                ? `&redirect_url=${encodeURIComponent(prev_loc)}`
                : '';
            
            await client.get(
                `/teacher/profile/signin?email=${encodeURIComponent(email)}${redirectParam}`
            );
            navigate(`/email-sent`, { state: { email: email, back: loc.pathname } });
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message || "Some error occurred");
            } else {
                setError("An unexpected error occurred. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <FormLayout showBackButton={false}>
            <Typography variant="h6" align="center">
                {isSignup ? SIGNUP_HEADER : SIGNIN_HEADER}
            </Typography>
            <TextField
                fullWidth
                error={error ? true : false}
                helperText={error}
                label={SIGNIN_WITH_EMAIL_EMAIL}
                value={email}
                onChange={(e) => emailChanged(e.target.value)}
                onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                        submitEmail();
                    }
                }}
            />
            <Button
                fullWidth
                disabled={loading}
                variant="contained"
                onClick={submitEmail}
                sx={{ boxShadow: "none", py: 2, lineHeight: 1 }}
            >
                {SIGNIN_WITH_EMAIL_SUBMIT}
            </Button>
            <Typography align="center">
                or continue with
            </Typography>
            <Button
                variant='contained'
                fullWidth
                disabled={loading}
                onClick={() => auth.signin.withApple()}
                sx={{ 
                    boxShadow: 'none', 
                    py: 2, 
                    lineHeight: 1,
                    backgroundColor: '#212121',
                    color: '#fff',
                    '&:hover': {
                        backgroundColor: '#333',
                    }
                }}
            >
                {SIGNIN_BUTTON_APPLE}
            </Button>
            {loading && <CircularProgress sx={{ alignSelf: "center" }} />}
        </FormLayout>
    );
};